// Next
import DocumentTitle from '@/components/ui/DocumentTitle'

// Hooks
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Components
import DashboardFulfillmentOrdersWaiting from './DashboardFulfillmentOrdersWaiting'
import DashboardProductionOrders from './DashboardProductionOrders'
import DashboardComponentsInventory from './DashboardComponentsInventory'
import DashboardCharts from './DashboardCharts'
import useApiSWR from '@/hooks/useApiSWR'

// User
import useMe from '@/hooks/useMe'

// Mantine
import { ActionIcon, Tooltip } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'

// SWR
import useSWR from 'swr'
import { mutate } from 'swr'

// Layout
import ScrollableScreen from '@/components/ui/ScrollableScreen'

/**
 * Main dashboard component displaying key metrics and analytics
 * @returns {JSX.Element} Dashboard layout with widgets and charts
 */
export default function Dashboard() {
    // Check if user is admin
    const { isAdmin } = useMe()

    const { requestGet } = useAxiosApi()

    // Fetch dashboard data with SWR
    const {
        data: dashboardData,
        isLoading: dashboardDataLoading,
        isValidating: dashboardDataValidating,
    } = useSWR('/dashboard', requestGet, {
        refreshInterval: 60000, // 60 seconds - SWR automatically pauses when tab is not visible
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
    })

    // Fetch inventory data with SWR
    const {
        data: inventoryData,
        isLoading: inventoryDataLoading,
        isValidating: inventoryDataValidating,
    } = useSWR('/dashboard/inventory', requestGet, {
        refreshInterval: 60000, // 60 seconds - SWR automatically pauses when tab is not visible
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
    })

    const charts = useApiSWR(isAdmin ? '/dashboard/charts' : null, null, {
        refreshInterval: 60000,
    })

    // Trigger refetch manually
    const triggerRefetch = () => {
        mutate('/dashboard')
        mutate('/dashboard/inventory')
        if (isAdmin) charts.mutate()
    }

    // Loading state
    const isLoading =
        charts.isLoading ||
        dashboardDataLoading ||
        inventoryDataLoading ||
        dashboardDataValidating ||
        inventoryDataValidating

    const fulfillment_stats_by_location = isAdmin
        ? dashboardData?.data?.fulfillment_stats_by_location
        : dashboardData?.data?.fulfillment_stats_by_location?.filter(
              (i) => i.location_id === 1
          )

    const production_orders = isAdmin
        ? dashboardData?.data?.production_orders
        : dashboardData?.data?.production_orders?.filter(
              (i) => i.location_id === 1
          )

    const renderOperationalMetrics = () => (
        <section className="grid grid-cols-1 @5xl:grid-cols-2 @7xl:grid-cols-2 gap-5">
            <DashboardFulfillmentOrdersWaiting
                fulfillment_stats_by_location={fulfillment_stats_by_location}
                last_fetch={dashboardData?.data?.current_time}
                fulfillments_orders_address_errors={
                    dashboardData?.data
                        ?.fulfillments_orders_address_verification_error
                }
            />

            <DashboardProductionOrders production_orders={production_orders} />
        </section>
    )

    const inventoryLocations = [6, 8, 3, 7]
    const visibleInventory = inventoryLocations
        .map((id) => inventoryData?.data?.find((item) => item.location.id === id))
        .filter(Boolean)

    const renderInventoryOverview = () => (
        <section className="space-y-3 rounded-xl border bg-white p-4" aria-label="Inventaires">
            <h2 className="font-medium">Inventaires</h2>
            <div className="grid grid-cols-1 @3xl:grid-cols-2 @7xl:grid-cols-4 gap-5">
                {!inventoryData &&
                    Array.from({ length: 4 }).map((_, index) => (
                        <div
                            key={index}
                            className="h-[76px] border animate-pulse bg-gray-200 rounded-xl"
                        />
                    ))}

                {visibleInventory.map((location, index) => (
                    <DashboardComponentsInventory
                        key={location?.location?.id || index}
                        inventoryData={location}
                    />
                ))}
            </div>
        </section>
    )

    // Breadcrumbs
    const breadcrumbs = [
        {
            label: 'Dashboard',
        },
    ]

    // Right section
    const rightSection = (
        <Tooltip label="Actualiser les données">
            <ActionIcon
                onClick={triggerRefetch}
                variant="default"
                size="lg"
                loading={isLoading}
            >
                <IconRefresh size={16} />
            </ActionIcon>
        </Tooltip>
    )

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            <DocumentTitle title="BO - Dashboard - Baltic Watches" />

            <main className="@container w-full">
                <div className="p-5 space-y-5">
                    {renderOperationalMetrics()}
                    {isAdmin && renderInventoryOverview()}
                    {isAdmin && <DashboardCharts data={charts.data} loading={charts.isLoading} error={charts.error} retry={() => charts.mutate()} />}
                </div>
            </main>
        </ScrollableScreen>
    )
}
